import React, { useMemo, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
} from "@xyflow/react";
import { Position, MarkerType } from "@xyflow/system";
import "@xyflow/react/dist/style.css";
import {
  FaProjectDiagram,
  FaSearch,
  FaTimes,
  FaPlay,
  FaTasks,
  FaCodeBranch,
  FaBell,
  FaRegFileAlt,
  FaExchangeAlt,
  FaRegClock,
  FaFlagCheckered,
} from "react-icons/fa";
import {
  colors,
  spacing,
  radius,
  shadows,
  typography,
  cardBase,
  pageContainer,
  inputBase,
} from "../../utils/designTokens";
import ScreenHeader from "../../components/ScreenHeader";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import { getProcessesByCategory, getProcessById } from "../../processes/registry";
import { STEP_TYPE_META, STATUS_META, laneColor } from "../../processes/schema";

// ─── Constantes de layout del diagrama ─────────────────────────────────────────
const LANE_LABEL_W = 172; // ancho de la columna de etiquetas de carril
const COL_W = 236; // ancho por columna de pasos
const LANE_H = 132; // alto mínimo de un carril
const NODE_W = 196;
const NODE_H = 66;
const TOP = 16;
const STACK_GAP = 16; // separación vertical entre pasos del mismo carril/columna

const STEP_ICONS = {
  start: FaPlay,
  task: FaTasks,
  decision: FaCodeBranch,
  notification: FaBell,
  document: FaRegFileAlt,
  handoff: FaExchangeAlt,
  wait: FaRegClock,
  end: FaFlagCheckered,
};

// ─── Nodos custom de React Flow ────────────────────────────────────────────────

function LaneBand({ data }) {
  const c = data.color;
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        background: c.bg,
        borderTop: `1px solid ${c.border}`,
        borderBottom: `1px solid ${c.border}`,
        borderRadius: radius.sm,
        display: "flex",
        alignItems: "stretch",
        opacity: 0.55,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          width: LANE_LABEL_W,
          flexShrink: 0,
          background: c.bg,
          borderRight: `1px solid ${c.border}`,
          display: "flex",
          alignItems: "center",
          padding: `0 ${spacing[3]}px`,
        }}
      >
        <span
          style={{
            fontSize: typography.sizes.sm,
            color: c.text,
            fontWeight: 400,
            textTransform: "uppercase",
            letterSpacing: "0.4px",
            lineHeight: 1.25,
          }}
        >
          {data.actor}
        </span>
      </div>
    </div>
  );
}

function StepNode({ data, selected }) {
  const meta = STEP_TYPE_META[data.step.type] || STEP_TYPE_META.task;
  const Icon = STEP_ICONS[data.step.type] || FaTasks;
  return (
    <div
      style={{
        width: NODE_W,
        minHeight: NODE_H,
        background: colors.white,
        border: `1px solid ${selected ? colors.primary : meta.border}`,
        borderLeft: `4px solid ${meta.color}`,
        borderRadius: radius.sm,
        boxShadow: selected ? `0 0 0 3px ${colors.primaryLighter}` : shadows.sm,
        padding: `${spacing[2]}px ${spacing[3]}px`,
        cursor: "pointer",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <Handle type="target" position={Position.Left} style={handleStyle} />
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 18,
            height: 18,
            borderRadius: radius.full,
            background: meta.bg,
            color: meta.color,
            flexShrink: 0,
          }}
        >
          <Icon size={9} />
        </span>
        <span
          style={{
            fontSize: typography.sizes.xs,
            color: meta.color,
            textTransform: "uppercase",
            letterSpacing: "0.3px",
          }}
        >
          {meta.label}
        </span>
      </div>
      <span
        style={{
          fontSize: typography.sizes.base,
          color: colors.black,
          lineHeight: 1.3,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {data.step.label}
      </span>
      {data.step.sla && (
        <span style={{ fontSize: 10, color: colors.warningDark }}>⏱ {data.step.sla}</span>
      )}
      <Handle type="source" position={Position.Right} style={handleStyle} />
    </div>
  );
}

const handleStyle = {
  width: 6,
  height: 6,
  background: colors.gray400,
  border: "none",
};

const nodeTypes = { laneBand: LaneBand, stepNode: StepNode };

// ─── Cálculo del layout en carriles ────────────────────────────────────────────

function buildGraph(process) {
  const { lanes, steps, transitions } = process;
  const laneIndex = {};
  lanes.forEach((l, i) => (laneIndex[l.id] = i));

  // Columna por orden topológico (longest-path desde los orígenes).
  const rank = {};
  steps.forEach((s) => (rank[s.id] = 0));
  for (let iter = 0; iter < steps.length; iter++) {
    let changed = false;
    for (const t of transitions) {
      if ((rank[t.to] ?? 0) < (rank[t.from] ?? 0) + 1) {
        rank[t.to] = (rank[t.from] ?? 0) + 1;
        changed = true;
      }
    }
    if (!changed) break;
  }
  const maxCol = steps.reduce((m, s) => Math.max(m, rank[s.id]), 0);

  // Apilado: pasos que caen en el mismo (carril, columna).
  const slotCounts = {};
  steps.forEach((s) => {
    const key = `${laneIndex[s.laneId]}:${rank[s.id]}`;
    slotCounts[key] = (slotCounts[key] || 0) + 1;
  });
  // Alto de cada carril según el máximo apilamiento.
  const laneStack = lanes.map((_, i) => {
    let max = 1;
    for (let c = 0; c <= maxCol; c++) max = Math.max(max, slotCounts[`${i}:${c}`] || 0);
    return max;
  });
  const laneHeight = laneStack.map((n) =>
    n <= 1 ? LANE_H : n * NODE_H + (n - 1) * STACK_GAP + 32
  );
  const laneY = [];
  laneHeight.reduce((acc, h, i) => {
    laneY[i] = TOP + acc;
    return acc + h;
  }, 0);

  const totalWidth = LANE_LABEL_W + (maxCol + 1) * COL_W + spacing[6];
  const totalHeight = (laneY[lanes.length - 1] || TOP) + (laneHeight[lanes.length - 1] || LANE_H) + TOP;

  // Nodos de carril (al fondo).
  const laneNodes = lanes.map((l, i) => ({
    id: `lane-${l.id}`,
    type: "laneBand",
    position: { x: 0, y: laneY[i] },
    data: { actor: l.actor, color: l.color ? { bg: l.color, border: l.color, text: colors.gray900 } : laneColor(l.actor) },
    draggable: false,
    selectable: false,
    zIndex: 0,
    style: { width: totalWidth, height: laneHeight[i] },
  }));

  // Nodos de paso (encima), apilados dentro de su carril/columna.
  const placed = {};
  const stepNodes = steps.map((s) => {
    const i = laneIndex[s.laneId];
    const col = rank[s.id];
    const key = `${i}:${col}`;
    const k = placed[key] || 0;
    placed[key] = k + 1;
    const count = slotCounts[key] || 1;
    const stackH = count * NODE_H + (count - 1) * STACK_GAP;
    const startY = laneY[i] + (laneHeight[i] - stackH) / 2;
    return {
      id: s.id,
      type: "stepNode",
      position: {
        x: LANE_LABEL_W + col * COL_W + (COL_W - NODE_W) / 2,
        y: startY + k * (NODE_H + STACK_GAP),
      },
      data: { step: s, lane: process.lanes[i] },
      draggable: false,
      zIndex: 1,
    };
  });

  const edges = transitions.map((t, idx) => ({
    id: `e-${t.from}-${t.to}-${idx}`,
    source: t.from,
    target: t.to,
    label: t.label || undefined,
    type: "smoothstep",
    markerEnd: { type: MarkerType.ArrowClosed, color: colors.gray400, width: 16, height: 16 },
    style: { stroke: colors.gray400, strokeWidth: 1.5 },
    labelStyle: { fontSize: 11, fill: colors.gray700, fontFamily: typography.fontFamily },
    labelBgStyle: { fill: colors.white },
    labelBgPadding: [4, 2],
    labelBgBorderRadius: 4,
  }));

  return { nodes: [...laneNodes, ...stepNodes], edges, width: totalWidth, height: totalHeight };
}

// ─── Pantalla ──────────────────────────────────────────────────────────────────

export default function Procesos() {
  const groups = useMemo(() => getProcessesByCategory(), []);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedStepId, setSelectedStepId] = useState(null);

  const process = selectedId ? getProcessById(selectedId) : null;
  const graph = useMemo(() => (process ? buildGraph(process) : null), [process]);
  const selectedStep =
    process && selectedStepId ? process.steps.find((s) => s.id === selectedStepId) : null;
  const selectedStepLane =
    selectedStep && process ? process.lanes.find((l) => l.id === selectedStep.laneId) : null;

  const q = query.trim().toLowerCase();
  const filteredGroups = q
    ? groups
        .map((g) => ({
          ...g,
          processes: g.processes.filter((p) =>
            [p.name, p.outcome, g.meta.label, (p.actors || []).join(" ")]
              .join(" ")
              .toLowerCase()
              .includes(q)
          ),
        }))
        .filter((g) => g.processes.length > 0)
    : groups;

  const totalCount = groups.reduce((n, g) => n + g.processes.length, 0);

  const handleSelectProcess = (id) => {
    setSelectedId(id);
    setSelectedStepId(null);
  };

  return (
    <div style={pageContainer}>
      <ScreenHeader
        icon={FaProjectDiagram}
        title="Procesos"
        subtitle={`Mapa operacional de Habitia · ${totalCount} procesos · diagramas por actor`}
      />

      <div style={styles.layout}>
        {/* Selector lateral */}
        <aside style={styles.sidebar}>
          <div style={{ position: "relative", marginBottom: spacing[3] }}>
            <FaSearch
              size={12}
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: colors.gray400 }}
            />
            <input
              style={{ ...inputBase, paddingLeft: 32 }}
              placeholder="Buscar proceso o actor…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Buscar proceso"
            />
          </div>

          <div style={styles.sidebarScroll}>
            {filteredGroups.length === 0 && (
              <p style={styles.noResults}>Sin resultados para “{query}”.</p>
            )}
            {filteredGroups.map((g) => (
              <div key={g.category} style={{ marginBottom: spacing[4] }}>
                <div style={styles.groupHeader}>
                  <span style={{ ...styles.groupDot, background: g.meta.accent }} />
                  <span style={styles.groupLabel}>{g.meta.label}</span>
                  <span style={styles.groupCount}>{g.processes.length}</span>
                </div>
                {g.processes.map((p) => {
                  const active = p.id === selectedId;
                  return (
                    <button
                      key={p.id}
                      onClick={() => handleSelectProcess(p.id)}
                      style={{
                        ...styles.processRow,
                        ...(active ? styles.processRowActive : {}),
                      }}
                    >
                      <span style={styles.processName}>{p.name}</span>
                      <StatusBadge status={STATUS_META[p.status].key} size="sm" />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </aside>

        {/* Diagrama */}
        <main style={styles.main}>
          {!process && (
            <div style={styles.emptyWrap}>
              <EmptyState
                icon={FaProjectDiagram}
                title="Elegí un proceso"
                description="Seleccioná un proceso de la lista para ver su diagrama con carriles por actor, decisiones y handoffs."
              />
            </div>
          )}

          {process && (
            <>
              <div style={styles.processHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: spacing[3], flexWrap: "wrap" }}>
                  <h3 style={styles.processTitle}>{process.name}</h3>
                  <StatusBadge status={STATUS_META[process.status].key} size="sm" />
                </div>
                <p style={styles.processOutcome}>{process.outcome}</p>
                <div style={styles.metaRow}>
                  <span style={styles.metaItem}>
                    <strong style={styles.metaKey}>Disparador:</strong> {process.trigger}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: spacing[2] }}>
                  {process.actors.map((a) => {
                    const c = laneColor(a);
                    return (
                      <span
                        key={a}
                        style={{
                          fontSize: typography.sizes.xs,
                          color: c.text,
                          background: c.bg,
                          border: `1px solid ${c.border}`,
                          borderRadius: radius.full,
                          padding: "2px 10px",
                        }}
                      >
                        {a}
                      </span>
                    );
                  })}
                </div>
              </div>

              <div style={styles.diagramWrap}>
                <ReactFlow
                  key={process.id}
                  nodes={graph.nodes}
                  edges={graph.edges}
                  nodeTypes={nodeTypes}
                  fitView
                  fitViewOptions={{ padding: 0.18 }}
                  minZoom={0.2}
                  maxZoom={1.6}
                  nodesDraggable={false}
                  nodesConnectable={false}
                  elementsSelectable
                  proOptions={{ hideAttribution: true }}
                  onNodeClick={(_, node) => {
                    if (node.type === "stepNode") setSelectedStepId(node.id);
                  }}
                  onPaneClick={() => setSelectedStepId(null)}
                >
                  <Background color={colors.gray200} gap={20} />
                  <Controls showInteractive={false} />
                  <MiniMap
                    pannable
                    zoomable
                    nodeColor={(n) =>
                      n.type === "laneBand"
                        ? (n.data?.color?.bg || colors.gray100)
                        : (STEP_TYPE_META[n.data?.step?.type]?.color || colors.gray400)
                    }
                    style={{ background: colors.gray50 }}
                  />
                </ReactFlow>

                {/* Panel de detalle del paso */}
                {selectedStep && (
                  <div style={styles.detailPanel}>
                    <div style={styles.detailHeader}>
                      <span
                        style={{
                          fontSize: typography.sizes.xs,
                          color: STEP_TYPE_META[selectedStep.type].color,
                          background: STEP_TYPE_META[selectedStep.type].bg,
                          border: `1px solid ${STEP_TYPE_META[selectedStep.type].border}`,
                          borderRadius: radius.full,
                          padding: "2px 10px",
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                        }}
                      >
                        {STEP_TYPE_META[selectedStep.type].label}
                      </span>
                      <button
                        onClick={() => setSelectedStepId(null)}
                        style={styles.detailClose}
                        aria-label="Cerrar detalle"
                      >
                        <FaTimes size={13} />
                      </button>
                    </div>
                    <h4 style={styles.detailTitle}>{selectedStep.label}</h4>

                    <div style={styles.detailField}>
                      <span style={styles.detailKey}>Responsable</span>
                      <span style={styles.detailValue}>{selectedStepLane?.actor || "—"}</span>
                    </div>
                    {selectedStep.description && (
                      <div style={styles.detailField}>
                        <span style={styles.detailKey}>Descripción</span>
                        <span style={styles.detailValue}>{selectedStep.description}</span>
                      </div>
                    )}
                    {selectedStep.sla && (
                      <div style={styles.detailField}>
                        <span style={styles.detailKey}>Plazo / SLA</span>
                        <span style={styles.detailValue}>{selectedStep.sla}</span>
                      </div>
                    )}
                    {selectedStep.evidence && (
                      <div style={styles.detailField}>
                        <span style={styles.detailKey}>Evidencia</span>
                        <code style={styles.detailEvidence}>{selectedStep.evidence}</code>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const PANEL_H = "calc(100vh - 168px)";

const styles = {
  layout: {
    display: "flex",
    gap: spacing[4],
    alignItems: "stretch",
    height: PANEL_H,
  },
  sidebar: {
    width: 320,
    flexShrink: 0,
    ...cardBase,
    padding: spacing[4],
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  sidebarScroll: {
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
    paddingRight: 4,
  },
  noResults: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    textAlign: "center",
    marginTop: spacing[6],
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: `${spacing[2]}px ${spacing[1]}px`,
    position: "sticky",
    top: 0,
    background: colors.bgCard,
    zIndex: 1,
  },
  groupDot: { width: 8, height: 8, borderRadius: radius.full, flexShrink: 0 },
  groupLabel: {
    fontSize: typography.sizes.xs,
    color: colors.gray600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    flex: 1,
  },
  groupCount: { fontSize: typography.sizes.xs, color: colors.gray400 },
  processRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing[2],
    padding: `${spacing[2]}px ${spacing[3]}px`,
    border: "1px solid transparent",
    borderRadius: radius.sm,
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: typography.fontFamily,
    marginBottom: 2,
    transition: "background 0.15s ease, border-color 0.15s ease",
  },
  processRowActive: {
    background: colors.primarySubtle,
    border: `1px solid ${colors.primaryLight}`,
  },
  processName: {
    fontSize: typography.sizes.base,
    color: colors.gray900,
    lineHeight: 1.3,
  },
  main: {
    flex: 1,
    minWidth: 0,
    ...cardBase,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  emptyWrap: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  processHeader: {
    padding: `${spacing[4]}px ${spacing[5]}px`,
    borderBottom: `1px solid ${colors.gray200}`,
    flexShrink: 0,
  },
  processTitle: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.primary,
    margin: 0,
  },
  processOutcome: {
    fontSize: typography.sizes.md,
    color: colors.gray700,
    margin: `${spacing[2]}px 0 0`,
    lineHeight: 1.4,
  },
  metaRow: { marginTop: spacing[2] },
  metaItem: { fontSize: typography.sizes.sm, color: colors.gray600, lineHeight: 1.4 },
  metaKey: { color: colors.gray900, fontWeight: 400 },
  diagramWrap: { flex: 1, position: "relative", minHeight: 0 },
  detailPanel: {
    position: "absolute",
    top: spacing[3],
    right: spacing[3],
    width: 300,
    maxHeight: "calc(100% - 24px)",
    overflowY: "auto",
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.lg,
    border: `1px solid ${colors.gray200}`,
    padding: spacing[4],
    zIndex: 5,
  },
  detailHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing[2],
  },
  detailClose: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    border: "none",
    background: colors.gray100,
    color: colors.gray600,
    borderRadius: radius.sm,
    cursor: "pointer",
  },
  detailTitle: {
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.black,
    margin: `0 0 ${spacing[3]}px`,
    lineHeight: 1.35,
  },
  detailField: { marginBottom: spacing[3], display: "flex", flexDirection: "column", gap: 2 },
  detailKey: {
    fontSize: typography.sizes.xs,
    color: colors.gray400,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  detailValue: { fontSize: typography.sizes.base, color: colors.gray900, lineHeight: 1.45 },
  detailEvidence: {
    fontSize: 11,
    fontFamily: typography.monoFamily,
    color: colors.primaryDark,
    background: colors.primarySubtle,
    padding: "3px 6px",
    borderRadius: radius.sm,
    wordBreak: "break-all",
  },
};
